// @ts-nocheck
import React, { useState, useEffect } from 'react';

interface AdminKeyboardShortcutsProps {
  className?: string;
}

export const AdminKeyboardShortcuts: React.FC<AdminKeyboardShortcutsProps> = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in inputs
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return;
      }

      // Press ? to toggle shortcuts help
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey) {
        e.preventDefault();
        setIsOpen(!isOpen);
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  const shortcuts = [
    { keys: ['⌘', '/', 'Ctrl', '+', '?'], description: 'Show keyboard shortcuts' },
    { keys: ['⌘', '/', 'Ctrl', '+', 'M'], description: 'Toggle sidebar menu' },
    { keys: ['⌘', '/', 'Ctrl', '+', '1'], description: 'Navigate to Dashboard' },
    { keys: ['⌘', '/', 'Ctrl', '+', '2'], description: 'Navigate to Users' },
    { keys: ['⌘', '/', 'Ctrl', '+', '3'], description: 'Navigate to Topics' },
    { keys: ['⌘', '/', 'Ctrl', '+', '4'], description: 'Navigate to Subscriptions' },
    { keys: ['⌘', '/', 'Ctrl', '+', '5'], description: 'Navigate to Notifications' },
    { keys: ['⌘', '/', 'Ctrl', '+', '6'], description: 'Navigate to Contacts' },
    { keys: ['⌘', '/', 'Ctrl', '+', '7'], description: 'Navigate to News' },
    { keys: ['⌘', '/', 'Ctrl', '+', '8'], description: 'Navigate to Media Library' },
    { keys: ['⌘', '/', 'Ctrl', '+', '9'], description: 'Navigate to Analytics' },
    { keys: ['⌘', '/', 'Ctrl', '+', '0'], description: 'Navigate to Settings' },
    { keys: ['Esc'], description: 'Close dialogs/sidebar' },
  ];

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-4 right-4 z-40 p-3 bg-luna-secondary text-white rounded-full shadow-lg hover:bg-luna-secondary/90 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation ${className}`}
        aria-label="Show keyboard shortcuts"
        title="Press ? to show keyboard shortcuts"
      >
        <span className="material-symbols-outlined">keyboard</span>
      </button>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
        onClick={() => setIsOpen(false)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="shortcuts-title"
      >
        {/* Modal */}
        <div
          className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-6 max-w-2xl w-full shadow-xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-start mb-4">
            <h3 id="shortcuts-title" className="text-xl font-bold text-luna-text-light dark:text-luna-text-dark">
              Keyboard Shortcuts
            </h3>
            <button
              onClick={() => setIsOpen(false)}
              className="text-luna-text-light/50 dark:text-luna-text-dark/50 hover:text-luna-text-light dark:hover:text-luna-text-dark p-2 -mt-2 -mr-2 min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
              aria-label="Close shortcuts"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          <div className="space-y-3">
            {shortcuts.map((shortcut, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-2 border-b border-luna-secondary/10 last:border-b-0"
              >
                <span className="text-sm text-luna-text-light dark:text-luna-text-dark">
                  {shortcut.description}
                </span>
                <div className="flex items-center gap-1">
                  {shortcut.keys.map((key, keyIndex) => (
                    <React.Fragment key={keyIndex}>
                      {keyIndex > 0 && <span className="text-luna-text-light/30 dark:text-luna-text-dark/30">+</span>}
                      <kbd className="px-2 py-1 text-xs font-mono bg-luna-secondary/10 dark:bg-luna-secondary/20 text-luna-text-light dark:text-luna-text-dark rounded border border-luna-secondary/30 min-w-[32px] text-center">
                        {key}
                      </kbd>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-luna-secondary/10">
            <p className="text-xs text-luna-text-light/50 dark:text-luna-text-dark/50 text-center">
              Press <kbd className="px-1.5 py-0.5 text-xs bg-luna-secondary/10 rounded border border-luna-secondary/30">Esc</kbd> or click outside to close
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
